import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import "./login.css";
import loginIcon from "./image.png"; // ✅ Import your image here

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepLoggedIn, setKeepLoggedIn] = useState(true); // ✅ Keep me logged in
  const [error, setError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();

  // ✅ Check if user is already logged in on page load
  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        const userId = session.user.id;
        const { data: userInfo, error: userError } = await supabase
          .from("users")
          .select("role, is_blocked")
          .eq("id", userId)
          .single();

        if (userError || !userInfo) {
          await supabase.auth.signOut();
          return;
        }

        if (userInfo.is_blocked) {
          await supabase.auth.signOut();
          setError("Your account has been blocked by the admin.");
          return;
        }

        const role = userInfo.role;
        if (role === "admin") navigate("/admin-dashboard");
        else if (role === "teacher") navigate("/teacher-dashboard");
        else if (role === "student") navigate("/student-dashboard");
      }
    };

    checkSession();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsRedirecting(true);

    const { data, error: loginError } = await supabase.auth.signInWithPassword(
      {
        email,
        password,
      },
      {
        // ✅ Persist session based on checkbox
        persistSession: keepLoggedIn,
      }
    );

    if (loginError) {
      setError(loginError.message);
      setIsRedirecting(false);
      return;
    }

    const userId = data.user.id;

    const { data: userInfo, error: userError } = await supabase
      .from("users")
      .select("role, is_blocked")
      .eq("id", userId)
      .single();

    if (userError || !userInfo) {
      await supabase.auth.signOut();
      setError("User info not found.");
      setIsRedirecting(false);
      return;
    }

    if (userInfo.is_blocked) {
      await supabase.auth.signOut();
      setError("Your account has been blocked by the admin.");
      setIsRedirecting(false);
      return;
    }

    const role = userInfo.role;

    const { error: updateError } = await supabase.auth.updateUser({
      data: { role },
    });

    if (updateError) {
      setError("Failed to update user metadata.");
      setIsRedirecting(false);
      return;
    }

    if (role === "admin") navigate("/admin-dashboard");
    else if (role === "teacher") navigate("/teacher-dashboard");
    else if (role === "student") navigate("/student-dashboard");
    else {
      setError("Unknown user role.");
      setIsRedirecting(false);
    }
  };

  return (
    <div className="login-container">
      {/* Floating Background Circles */}
      <div className="floating-circle large"></div>
      <div className="floating-circle medium"></div>
      <div className="floating-circle small"></div>

      {/* Login Card */}
      <div
        className={`login-card ${isRedirecting ? "login-card-loading" : ""}`}
      >
        <div className="login-header">
          <div className="login-icon">
            <img
              src={loginIcon}
              alt="Login Icon"
              style={{ width: "90px", height: "90px", borderRadius: "50%" }}
            />
          </div>
          <h1 className="login-title">Awareness Paradigm</h1>
          <p className="login-subtitle">
            Transform your inner world through self-awareness
          </p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <input
            className="login-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isRedirecting}
          />
          <input
            className="login-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isRedirecting}
          />

          {/* ✅ Keep me logged in checkbox */}
          <div className="login-checkbox-container">
            <input
              type="checkbox"
              id="keepLoggedIn"
              checked={keepLoggedIn}
              onChange={(e) => setKeepLoggedIn(e.target.checked)}
            />
            <label htmlFor="keepLoggedIn">Keep me logged in</label>
          </div>

          <button
            className="login-button"
            type="submit"
            disabled={isRedirecting}
          >
            {isRedirecting ? "Redirecting..." : "Begin Your Journey"}
          </button>
        </form>

        {error && <div className="login-error">{error}</div>}

        <div className="login-footer">
          Your journey to self-awareness starts here ✨
        </div>
      </div>

      {/* Page Footer */}
      <footer className="page-footer">
        LMS Awareness-Paradigm © 2025 — All rights reserved
      </footer>
    </div>
  );
}

export default LoginPage;
